import { useTranslation } from "react-i18next";
import { LANGUAGES, isLang, setLanguage } from "../i18n";

/**
 * Three codes, shown at once, for the same reason the theme control shows
 * three: a cycling button cannot say what it will do next, and "what language
 * is this in" is exactly the question somebody has when the page is already in
 * a language they cannot read - at which point a button labelled with a word
 * is no help either.
 *
 * CODES, NOT FLAGS. A flag is a country, and the three here are languages:
 * Finnish is spoken by people who are not in Finland, English by almost
 * nobody in this team as a first language, and there is no flag for either
 * that is not somebody's politics. Two letters are also the same width in
 * every language, which keeps the header from reflowing when it is used.
 *
 * The native name is the title and the accessible name - "Polski", not
 * "Polish" - because a reader looking for their own language is looking for
 * the word they would use for it.
 */
export default function LangToggle() {
  const { t, i18n } = useTranslation();
  const current = isLang(i18n.resolvedLanguage) ? i18n.resolvedLanguage : "en";

  return (
    <div
      role="radiogroup"
      aria-label={t("lang.label")}
      className="flex items-center rounded-md bg-brand-800/60 p-0.5 dark:bg-brand-950/60"
    >
      {LANGUAGES.map((l) => {
        const on = current === l.code;
        return (
          <button
            key={l.code}
            role="radio"
            aria-checked={on}
            aria-label={l.name}
            title={l.name}
            lang={l.code}
            className={`flex h-6 w-7 items-center justify-center rounded text-[11px] font-semibold tracking-wide transition-colors ${
              on
                ? "bg-white text-brand-700"
                : "text-sky-200 hover:bg-brand-600 dark:hover:bg-brand-800"
            }`}
            onClick={() => setLanguage(l.code)}
          >
            {l.short}
          </button>
        );
      })}
    </div>
  );
}
